import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { VocabularyCard, VocabularyWord } from '../components/VocabularyCard';
// Mock Data
const MOCK_WORDS: VocabularyWord[] = [{
  id: '1',
  hiragana: 'たかい',
  kanji: '高い',
  romaji: 'takai',
  pitchPattern: [0, 1, 0],
  meaning: 'Expensive / Tall',
  mnemonic: "Imagine a TALL building that costs a lot - it's expensive and high up!",
  exampleSentence: 'このレストランは高いです。',
  exampleTranslation: 'This restaurant is expensive.'
}, {
  id: '2',
  hiragana: 'ねこ',
  kanji: '猫',
  romaji: 'neko',
  pitchPattern: [1, 0],
  meaning: 'Cat',
  mnemonic: 'The cat hurt its NECK-o looking up at the bird.',
  exampleSentence: '猫が好きです。',
  exampleTranslation: 'I like cats.'
}, {
  id: '3',
  hiragana: 'たべる',
  kanji: '食べる',
  romaji: 'taberu',
  pitchPattern: [0, 1, 0],
  meaning: 'To eat',
  mnemonic: 'I want to eat at a TABLE (taberu).',
  exampleSentence: '寿司を食べる。',
  exampleTranslation: 'I eat sushi.'
}, {
  id: '4',
  hiragana: 'みず',
  kanji: '水',
  romaji: 'mizu',
  pitchPattern: [0, 1],
  meaning: 'Water',
  mnemonic: 'Please give MIZU (me some) water.',
  exampleSentence: '水をください。',
  exampleTranslation: 'Water, please.'
}, {
  id: '5',
  hiragana: 'ほん',
  kanji: '本',
  romaji: 'hon',
  pitchPattern: [1, 0],
  meaning: 'Book',
  mnemonic: 'HONestly, I love reading books.',
  exampleSentence: '本を読みます。',
  exampleTranslation: 'I read books.'
}, {
  id: '6',
  hiragana: 'いぬ',
  kanji: '犬',
  romaji: 'inu',
  pitchPattern: [0, 1],
  meaning: 'Dog',
  mnemonic: 'I knew (inu) my dog would be loyal.',
  exampleSentence: '犬が走ります。',
  exampleTranslation: 'The dog runs.'
}, {
  id: '7',
  hiragana: 'あたらしい',
  kanji: '新しい',
  romaji: 'atarashii',
  pitchPattern: [0, 1, 0, 0, 0],
  meaning: 'New',
  mnemonic: 'A TARA SHE (atarashii) bought something new.',
  exampleSentence: '新しい車を買いました。',
  exampleTranslation: 'I bought a new car.'
}, {
  id: '8',
  hiragana: 'おおきい',
  kanji: '大きい',
  romaji: 'ookii',
  pitchPattern: [0, 0, 1, 0],
  meaning: 'Big',
  mnemonic: 'OH KEY (ookii) is so big I can barely hold it.',
  exampleSentence: '大きい家に住んでいます。',
  exampleTranslation: 'I live in a big house.'
}, {
  id: '9',
  hiragana: 'あかい',
  kanji: '赤い',
  romaji: 'akai',
  pitchPattern: [0, 1, 0],
  meaning: 'Red',
  mnemonic: 'A red KAI (akai) pepper is spicy.',
  exampleSentence: '赤いりんごが好きです。',
  exampleTranslation: 'I like red apples.'
}, {
  id: '10',
  hiragana: 'さむい',
  kanji: '寒い',
  romaji: 'samui',
  pitchPattern: [0, 1, 0],
  meaning: 'Cold',
  mnemonic: 'SOME WE (samui) feel cold in winter.',
  exampleSentence: '今日は寒いです。',
  exampleTranslation: 'Today is cold.'
}, {
  id: '11',
  hiragana: 'はやい',
  kanji: '速い',
  romaji: 'hayai',
  pitchPattern: [0, 1, 0],
  meaning: 'Fast',
  mnemonic: 'HAY! (hayai) That car is fast!',
  exampleSentence: '新幹線は速いです。',
  exampleTranslation: 'The bullet train is fast.'
}, {
  id: '12',
  hiragana: 'むずかしい',
  kanji: '難しい',
  romaji: 'muzukashii',
  pitchPattern: [0, 0, 1, 0, 0],
  meaning: 'Difficult',
  mnemonic: 'MUSIC CASH (muzukashii) is difficult to earn.',
  exampleSentence: '日本語は難しいです。',
  exampleTranslation: 'Japanese is difficult.'
}, {
  id: '13',
  hiragana: 'やすい',
  kanji: '安い',
  romaji: 'yasui',
  pitchPattern: [0, 1, 0],
  meaning: 'Cheap',
  mnemonic: 'YES! WE (yasui) found something cheap!',
  exampleSentence: 'この店は安いです。',
  exampleTranslation: 'This store is cheap.'
}, {
  id: '14',
  hiragana: 'おいしい',
  kanji: '美味しい',
  romaji: 'oishii',
  pitchPattern: [0, 1, 0, 0],
  meaning: 'Delicious',
  mnemonic: "OH! EASY (oishii) to eat when it's delicious.",
  exampleSentence: 'このラーメンは美味しいです。',
  exampleTranslation: 'This ramen is delicious.'
}];
// Helper function to select random words
function selectRandomWords(words: VocabularyWord[], count: number): VocabularyWord[] {
  const shuffled = [...words].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}
interface LessonPageProps {
  onComplete: (learnedWords: VocabularyWord[]) => void;
}
export function LessonPage({
  onComplete
}: LessonPageProps) {
  // Select 5 random words on mount
  const selectedWords = useMemo(() => selectRandomWords(MOCK_WORDS, 5), []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const handleNext = () => {
    if (currentIndex < selectedWords.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    } else {
      // Learning complete, return to home with learned words
      onComplete(selectedWords);
    }
  };
  const handlePrev = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };
  const progress = (currentIndex + 1) / selectedWords.length * 100;
  return <div className="min-h-screen bg-[#FDFBF7] text-gray-800 font-sans flex flex-col" data-id="element-94">
      {/* Header */}
      <header className="px-6 py-6 flex items-center justify-between sticky top-0 bg-[#FDFBF7]/80 backdrop-blur-sm z-10" data-id="element-95">
        <button onClick={() => onComplete([])} className="p-2 -ml-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-96">
          <X size={24} data-id="element-97" />
        </button>
        <div className="flex flex-col items-center" data-id="element-98">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1" data-id="element-99">
            New Words
          </span>
          <div className="flex items-center gap-2" data-id="element-100">
            <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-101">
              <motion.div className="h-full bg-[#E63946]" initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.3
            }} data-id="element-102" />
            </div>
            <span className="text-xs font-medium text-gray-500 w-8" data-id="element-103">
              {currentIndex + 1}/{selectedWords.length}
            </span>
          </div>
        </div>
        <div className="w-10" data-id="element-104" /> {/* Spacer for centering */}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-4 w-full max-w-md mx-auto relative overflow-y-auto pb-32" data-id="element-105">
        <AnimatePresence mode="wait" custom={direction} data-id="element-106">
          <VocabularyCard key={currentIndex} word={selectedWords[currentIndex]} direction={direction} data-id="element-107" />
        </AnimatePresence>
      </main>

      {/* Footer Controls - Fixed */}
      <footer className="fixed bottom-0 left-0 right-0 px-6 py-8 bg-[#FDFBF7]/95 backdrop-blur-sm border-t border-gray-100" data-id="element-108">
        <div className="flex items-center justify-between gap-4 w-full max-w-md mx-auto" data-id="element-109">
          <button onClick={handlePrev} disabled={currentIndex === 0} className={`
              p-4 rounded-2xl flex items-center justify-center transition-all duration-200
              ${currentIndex === 0 ? 'text-gray-300 bg-gray-100 cursor-not-allowed' : 'text-gray-600 bg-white shadow-sm hover:shadow-md hover:scale-105 active:scale-95'}
            `} data-id="element-110">
            <ChevronLeft size={24} data-id="element-111" />
          </button>

          <button onClick={handleNext} className="flex-1 bg-[#E63946] text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-red-200 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2" data-id="element-112">
            {currentIndex === selectedWords.length - 1 ? 'Finish' : 'Next Word'}
            <ChevronRight size={20} className="opacity-80" data-id="element-113" />
          </button>
        </div>
      </footer>
    </div>;
}